import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReportShell, { reportStyle, formatReportTimestamp } from '../../../shared/ReportShell';
import ReportOverlay from '../../../shared/ReportOverlay';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import '../../../shared/StyledInput.css';

// Purchasing's own Integrity Report — first occupant of the
// 'purchasing-reports' nav stub, same shell (ReportShell/ReportOverlay,
// filter panel + Run) as Inventory's own Product Integrity report, scoped
// by Rule instead of Category since the one check here (a Rebate Rule's
// Generate action skipping an in-scope SKU it couldn't calculate) is
// inherently Rule-specific. MVP has exactly one check, so no rule checklist
// yet — see vis_supplier_rebates.md's Future Scope.
function PurchasingIntegrityReportPage() {
  const navigate = useNavigate();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [rules, setRules] = useState([]);
  const [ruleId, setRuleId] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get('/api/supplier-rebate-rules', { params: { status: 'active' } }).then((res) => setRules(res.data.data || [])).catch(() => setRules([]));
  }, []);

  const selectedRule = rules.find((r) => String(r.id) === String(ruleId));

  async function handleRun() {
    setLoading(true);
    try {
      const res = await axios.get('/api/purchasing-integrity-report/run', { params: { rule_id: ruleId || undefined } });
      setResults(res.data.data);
      dismissError();
    } catch (err) {
      showError(err, 'Failed to run integrity report');
      setResults(null);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ width: '100%', maxWidth: 900, margin: '32px auto 48px' }}>
      <div style={{ background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: 24, maxWidth: 480 }}>
        <h2 style={{ margin: '0 0 16px', fontWeight: 600, fontSize: 20 }}>Purchasing Integrity</h2>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
          Every SKU a Supplier Rebate Rule's Generate action couldn't assign a rebate to — no Brand assigned, missing UoM1 and/or UoM2, or missing a Dealer Price for UoM1 and/or UoM2.
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Rebate Rule</div>
          <select className="styled-input" value={ruleId} onChange={(e) => setRuleId(e.target.value)} style={{ width: 260 }}>
            <option value="">All active Rules</option>
            {rules.map((r) => (
              <option key={r.id} value={r.id}>{r.name}</option>
            ))}
          </select>
        </div>

        <button type="button" onClick={handleRun} disabled={loading} style={{ padding: '8px 18px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.6 : 1 }}>
          {loading ? 'Running…' : 'Run'}
        </button>
        <button type="button" onClick={() => navigate('/purchasing-dashboard')} style={{ marginLeft: 8, padding: '8px 18px' }}>
          Cancel
        </button>
      </div>

      {results && (
        <ReportOverlay onClose={() => setResults(null)}>
          <ReportShell
            title="Purchasing Integrity Report"
            subtitle={(
              <>
                <div>{formatReportTimestamp()}</div>
                <div>Rebate Rule: {selectedRule ? selectedRule.name : 'All active Rules'}</div>
              </>
            )}
            onClose={() => setResults(null)}
          >
            {results.length === 0 ? (
              <div style={{ color: '#888', fontSize: 14 }}>No issues found.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: reportStyle.tableFontSize }}>
                <thead>
                  <tr className="print-only-row"><td colSpan={4} style={{ height: 18, padding: 0, border: 'none' }} /></tr>
                  <tr style={{ textAlign: 'left', borderBottom: reportStyle.tableHeaderBorder }}>
                    <th style={{ padding: '6px 8px 6px 0', width: 140 }}>SKU</th>
                    <th style={{ padding: '6px 8px 6px 0' }}>Name</th>
                    <th style={{ padding: '6px 8px 6px 0' }}>Rule</th>
                    <th style={{ padding: '6px 8px 6px 0' }}>Reason</th>
                  </tr>
                </thead>
                <tfoot>
                  <tr className="print-only-row"><td colSpan={4} style={{ height: 20, padding: 0, border: 'none' }} /></tr>
                </tfoot>
                <tbody>
                  {results.map((row) => (
                    <tr key={`${row.rule_id}-${row.sku}`} style={{ borderBottom: '1px solid #ddd', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                      <td style={{ padding: '6px 8px 6px 0', fontWeight: 600 }}>{row.sku}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.name}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.rule_name}</td>
                      <td style={{ padding: '6px 8px 6px 0' }}>{row.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ReportShell>
        </ReportOverlay>
      )}
    </div>
  );
}

export default PurchasingIntegrityReportPage;
